import * as React from 'react';
import { FC, memo, useCallback } from 'react';

import { OfferListItem } from 'components/offer-list-item/offer-list-item';
import Picture from 'components/picture';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import {
  isActiveWalmartUser,
  isWalmartOffer,
  isWalmartPaidMember,
  isWalmartQuarterlyOffer,
} from 'state/loyalty/hooks/utils/walmart';
import { OffersServiceMode } from 'state/loyalty/offers/types';
import { IncentiveSource } from 'state/loyalty/types';
import { TraceName, stopTrace } from 'utils/performance/trace';

import { IncentiveCardDescription } from '../incentive-card/incentive-card.styled';
import { ParsedRewardData } from '../incentive-details/types';

export interface ILoyaltyIOffersItem {
  handlePress: () => void;
  incentiveData: ParsedRewardData;
}

export const LoyaltyOffersItem: FC<ILoyaltyIOffersItem> = memo(
  ({ handlePress, incentiveData }) => {
    const { loyaltyUser } = useLoyaltyUser();
    const enableWalmartPlusIntegration = useFlag(LaunchDarklyFlag.ENABLE_WALMART_PLUS_INTEGRATION);
    const isWalmartUser = enableWalmartPlusIntegration && isActiveWalmartUser(loyaltyUser);

    const {
      image,
      imageDescription,
      name: incentiveName,
      description,
      offerServiceMode,
      metadata,
      inlineAlertMessage,
      endDate,
      expirationDate,
    } = incentiveData;

    const showPromoCodeLabel = metadata?.some(
      data => data?.value === IncentiveSource.PROMOTION_CODE
    );

    const promoCodeLabel = showPromoCodeLabel ? t('promoCodeBadgeLabel') : undefined;

    const onDisplay = useCallback(() => stopTrace(TraceName.OFFERS_LOAD), []);
    return (
      <OfferListItem
        id={incentiveData.id}
        engineId={incentiveData.loyaltyEngineId}
        image={
          <Picture
            image={image}
            alt={imageDescription}
            height="97px"
            width="97px"
            objectFitContain
            onDisplay={onDisplay}
          />
        }
        header={incentiveName}
        description={description && <IncentiveCardDescription content={description} />}
        serviceModeStyle={
          offerServiceMode === OffersServiceMode.DELIVERY_ONLY ? 'default-outline' : 'default-solid'
        }
        promoCodeLabel={promoCodeLabel}
        onPress={handlePress}
        unavailable={!!inlineAlertMessage}
        unavailableMessage={inlineAlertMessage}
        unavailableIcon="infoOutline"
        isWalmartOffer={isWalmartOffer(incentiveData) || isWalmartQuarterlyOffer(incentiveData)}
        isWalmartUser={isWalmartUser}
        isWalmartPaidMember={isWalmartUser && isWalmartPaidMember(loyaltyUser)}
        endDate={endDate}
        expirationDate={expirationDate}
        offerKind={incentiveData.offerKind}
      />
    );
  },
  (prev, next) => {
    return (
      prev.incentiveData.id === next.incentiveData.id &&
      prev.incentiveData.name === next.incentiveData.name &&
      prev.incentiveData.inlineAlertMessage === next.incentiveData.inlineAlertMessage
    );
  }
);
